import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { FlaskConical, Sparkles, Plus, X, Loader2, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArchetypeCard = ({ a, onChanged }) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const add = async (text) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/yvf/archetypes/${a.key}/criteria`, { text });
      toast.success('Criterion added');
      setDraft('');
      setSuggestions((s) => s ? s.filter((x) => x !== text) : s);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const remove = async (id) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/yvf/archetypes/${a.key}/criteria/${id}`);
      toast.success('Criterion removed');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const suggest = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/yvf/archetypes/${a.key}/suggest`);
      setSuggestions(r.data.suggestions);
    } catch (e) { toast.error('AI suggest failed'); }
    setBusy(false);
  };

  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 ${a.color}`} data-testid={`yvf-archetype-${a.key}`}>
      <p className="font-bold text-white">{a.label}</p>
      <p className="text-xs text-white/50 mt-0.5 mb-3">{a.description}</p>
      <div className="space-y-1.5">
        {(a.criteria || []).map((c, i) => (
          <div key={c.id} className="flex items-start gap-2 text-xs text-white/80 bg-black/20 rounded-md px-2 py-1.5">
            <span className="text-white/30 font-mono shrink-0">{i + 1}.</span>
            <span className="flex-1">{c.text}</span>
            <button onClick={() => remove(c.id)} disabled={busy} className="text-white/30 hover:text-red-300 shrink-0" data-testid={`yvf-crit-remove-${c.id}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && draft.trim().length >= 10) add(draft.trim()); }}
          placeholder="Add a criterion…"
          className="flex-1 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white outline-none focus:border-teal-500"
          data-testid={`yvf-crit-input-${a.key}`} />
        <button onClick={() => add(draft.trim())} disabled={busy || draft.trim().length < 10}
          className="p-1.5 rounded-md bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white" data-testid={`yvf-crit-add-${a.key}`}>
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button onClick={suggest} disabled={busy} title="AI suggest more criteria"
          className="p-1.5 rounded-md bg-purple-600/70 hover:bg-purple-500 disabled:opacity-40 text-white" data-testid={`yvf-crit-suggest-${a.key}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        </button>
      </div>
      {suggestions && (
        <div className="mt-2 space-y-1" data-testid={`yvf-suggestions-${a.key}`}>
          <p className="text-[10px] uppercase text-purple-300 font-bold">AI suggestions — tap to add</p>
          {suggestions.length === 0 && <p className="text-xs text-white/40 italic">No further suggestions.</p>}
          {suggestions.map((s) => (
            <button key={s} onClick={() => add(s)} className="block w-full text-left text-xs text-purple-200 bg-purple-500/10 border border-purple-500/30 rounded-md px-2 py-1.5 hover:bg-purple-500/20">
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminYvfCalibrationPage() {
  const [archs, setArchs] = useState(null);
  const [stats, setStats] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [humanScore, setHumanScore] = useState('');
  const [grading, setGrading] = useState(false);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [thresholdDraft, setThresholdDraft] = useState('70');
  const [savingThreshold, setSavingThreshold] = useState(false);

  useEffect(() => { if (archs?.threshold != null) setThresholdDraft(String(archs.threshold)); }, [archs?.threshold]);

  const saveThreshold = async () => {
    setSavingThreshold(true);
    try {
      const r = await axios.post(`${API}/admin/yvf/threshold`, { threshold: Number(thresholdDraft) });
      toast.success(`YVF pass mark set to ${r.data.threshold} — calls below it escalate`);
      loadArchs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save threshold'); }
    setSavingThreshold(false);
  };

  const toggleLive = async () => {
    const next = !archs?.live_enabled;
    if (!window.confirm(next
      ? 'Make YVF the LIVE grader for all new Yabbit calls? (Auto-falls back to standard on any YVF failure.)'
      : 'Switch Yabbit back to the standard grader?')) return;
    setToggling(true);
    try {
      await axios.post(`${API}/admin/yvf/rollout`, { enabled: next });
      toast.success(next ? 'YVF is now the live grader for Yabbit' : 'Yabbit back on the standard grader');
      loadArchs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed'); }
    setToggling(false);
  };

  const loadArchs = () => axios.get(`${API}/admin/yvf/archetypes`).then((r) => setArchs(r.data)).catch(() => toast.error('Failed to load YVF archetypes'));
  const loadStats = () => axios.get(`${API}/admin/yvf/shadow-grades`).then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadArchs(); loadStats(); }, []);

  const gradeManual = async () => {
    setGrading(true); setResult(null);
    try {
      const r = await axios.post(`${API}/admin/yvf/shadow-grade`, {
        transcript, human_score: humanScore === '' ? null : Number(humanScore),
      }, { timeout: 120000 });
      setResult(r.data);
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Grade failed'); }
    setGrading(false);
  };

  const runBatch = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/yvf/shadow-run`, { limit: 10 });
      toast.success(r.data.queued ? `${r.data.queued} calls queued — refresh stats in a minute or two` : r.data.message);
    } catch (e) { toast.error('Shadow run failed'); }
    setRunning(false);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-5" data-testid="yvf-page">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-teal-400" /> YVF — Yabbit VoiceFabric
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${archs?.live_enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-teal-500/20 text-teal-300'}`}>
              v1.0 · {archs?.live_enabled ? 'LIVE' : 'Shadow'}
            </span>
          </h1>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={toggleLive}
              disabled={toggling}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md text-white ${archs?.live_enabled ? 'bg-red-600/80 hover:bg-red-500' : 'bg-emerald-600 hover:bg-emerald-500'}`}
              data-testid="yvf-rollout-toggle"
            >
              {toggling ? 'Switching…' : archs?.live_enabled ? 'Switch Yabbit back to standard grader' : 'Make YVF the live grader for Yabbit'}
            </button>
            <span className="text-[11px] text-white/40">
              {archs?.live_enabled
                ? 'New Yabbit calls are being graded by YVF. Any YVF failure auto-falls back to the standard grader.'
                : 'Currently shadow-only — flip when calibration looks right.'}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-2" data-testid="yvf-threshold-row">
            <span className="text-xs text-white/50">YVF pass mark — escalate below</span>
            <input type="number" min="30" max="95" value={thresholdDraft}
              onChange={(e) => setThresholdDraft(e.target.value)}
              className="w-20 bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-white text-right outline-none focus:border-teal-500"
              data-testid="yvf-threshold-input" />
            <button onClick={saveThreshold}
              disabled={savingThreshold || Number(thresholdDraft) === (archs?.threshold ?? 70)}
              className="text-xs font-semibold px-2.5 py-1 rounded-md bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white"
              data-testid="yvf-threshold-save">
              {savingThreshold ? 'Saving…' : 'Save'}
            </button>
            <span className="text-[11px] text-white/30">Applies when YVF is the live grader. Voicemails and short voicemail calls are never graded.</span>
          </div>
          <p className="text-xs text-white/40 mt-1 max-w-2xl">
            IPVS Yabbit's own archetype set — telecom/UC/IT call categories with editable grading criteria.
            Shadow-only: nothing here touches production grades. Calibrate below, then decide on rollout.
          </p>
        </div>

        <div>
          <p className="text-sm font-semibold text-white mb-2">Archetypes &amp; criteria <span className="text-white/40 font-normal text-xs">— add/remove, or let AI suggest more per area</span></p>
          <div className="grid md:grid-cols-2 gap-3">
            {(archs?.archetypes || []).map((a) => <ArchetypeCard key={a.key} a={a} onChanged={loadArchs} />)}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="yvf-shadow-tool">
          <p className="text-sm font-semibold text-white mb-1">Shadow-grade a call</p>
          <p className="text-[11px] text-white/40 mb-2">Paste a transcript, optionally add your human score for comparison. Stored in <code>yvf_shadow_grades</code> — production untouched.</p>
          <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} rows={6}
            placeholder={"Agent: Good morning, ConvergeOne, this is Joanne…\nCaller: Hi, we're looking at upgrading our phone system…"}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono outline-none focus:border-teal-500"
            data-testid="yvf-transcript-input" />
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <input type="number" min="0" max="100" value={humanScore} onChange={(e) => setHumanScore(e.target.value)}
              placeholder="Human score (optional)" className="w-44 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white outline-none focus:border-teal-500"
              data-testid="yvf-human-score-input" />
            <Button onClick={gradeManual} disabled={grading || transcript.trim().length < 40} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="yvf-grade-btn">
              {grading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Grade this call
            </Button>
            <Button onClick={runBatch} disabled={running} variant="outline" className="border-white/20 text-white/80 hover:bg-white/10 bg-transparent" data-testid="yvf-shadow-run-btn">
              {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />} Shadow-run 10 recent Yabbit calls
            </Button>
          </div>
          {result && (
            <div className="mt-3 border border-teal-500/30 rounded-lg p-3 bg-teal-500/5" data-testid="yvf-grade-result">
              <p className="text-sm text-white font-semibold">
                {result.archetype_label} — <span className="text-teal-400">{result.overall_score}/100</span>
                {result.delta_vs_human != null && <span className={`ml-2 text-xs ${Math.abs(result.delta_vs_human) > 8 ? 'text-red-300' : 'text-emerald-400'}`}>Δ vs your score: {result.delta_vs_human > 0 ? '+' : ''}{result.delta_vs_human}</span>}
              </p>
              <div className="mt-2 space-y-1">
                {result.per_criterion.map((c) => (
                  <div key={c.id} className="text-xs flex items-start gap-2">
                    <span className={`font-bold tabular-nums w-9 shrink-0 ${c.status === 'na' ? 'text-white/30' : c.score_0_10 >= 7 ? 'text-emerald-400' : c.score_0_10 >= 4 ? 'text-amber-300' : 'text-red-300'}`}>{c.status === 'na' ? 'N/A' : c.score_0_10}</span>
                    <span className="text-white/70 flex-1">{c.text}{c.evidence && <span className="text-white/40 italic"> — "{c.evidence}"</span>}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="yvf-stats">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <p className="text-sm font-semibold text-white">Calibration window</p>
            <button onClick={loadStats} className="text-xs text-teal-400 hover:text-teal-300 inline-flex items-center gap-1" data-testid="yvf-stats-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {[['Shadow grades', stats?.count ?? '—'], ['Mean score', stats?.mean_score ?? '—'],
              ['Score range', stats?.score_range ? `${stats.score_range[0]}–${stats.score_range[1]}` : '—'],
              ['Mean Δ vs live', stats?.mean_delta_vs_live ?? '—'], ['Mean Δ vs human', stats?.mean_delta_vs_human ?? '—']].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{l}</p>
                <p className="text-lg font-bold text-white tabular-nums">{v}</p>
              </div>
            ))}
          </div>
          {(stats?.by_archetype || []).length > 0 && (
            <p className="text-xs text-white/50 mb-2">By archetype: {stats.by_archetype.map((b) => `${b.archetype} (${b.n}, mean ${b.mean})`).join(' · ')}</p>
          )}
          <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="yvf-recent-grades">
            {(stats?.recent || []).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-xs border-t border-white/10 py-1.5 flex-wrap">
                <span className="text-white/60">{r.graded_at?.slice(0, 16).replace('T', ' ')} · {r.archetype_label} · <span className="text-white/30">{r.source}</span></span>
                <span className="tabular-nums">
                  <b className="text-white/90">{r.overall_score}</b>
                  {r.delta_vs_live != null && <span className={`ml-2 ${Math.abs(r.delta_vs_live) > 8 ? 'text-red-300' : 'text-white/50'}`}>Δlive {r.delta_vs_live > 0 ? '+' : ''}{r.delta_vs_live}</span>}
                  {r.delta_vs_human != null && <span className="ml-2 text-teal-300">Δhuman {r.delta_vs_human > 0 ? '+' : ''}{r.delta_vs_human}</span>}
                </span>
              </div>
            ))}
            {(stats?.recent || []).length === 0 && <p className="text-xs text-white/40 italic">No shadow grades yet — grade a call above or run a batch.</p>}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
